import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

export type Appointment = Tables<'appointments'>;

export interface AppointmentDetail extends Appointment {
  participantIds: string[];
}

interface ParticipantEmbed {
  collaborator_id: string;
}

interface AppointmentQueryRow extends Appointment {
  appointment_participants: ParticipantEmbed[] | null;
}

const APPOINTMENT_SELECT =
  'id, title, description, starts_at, ends_at, dossier_id, event_type_id, created_by, created_at, updated_at, appointment_participants(collaborator_id)';

function toDetail(row: AppointmentQueryRow): AppointmentDetail {
  const { appointment_participants, ...rest } = row;
  return {
    ...rest,
    participantIds: (appointment_participants ?? []).map((p) => p.collaborator_id),
  };
}

export async function listAppointments(): Promise<AppointmentDetail[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .order('starts_at', { ascending: true });
  if (error) throw new Error(`Failed to list appointments: ${error.message}`);
  return ((data ?? []) as unknown as AppointmentQueryRow[]).map(toDetail);
}

export async function listDossierAppointments(dossierId: string): Promise<AppointmentDetail[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .eq('dossier_id', dossierId)
    .order('starts_at', { ascending: true });
  if (error) throw new Error(`Failed to list appointments: ${error.message}`);
  return ((data ?? []) as unknown as AppointmentQueryRow[]).map(toDetail);
}

export async function getAppointment(id: string): Promise<AppointmentDetail> {
  const { data, error } = await supabase
    .from('appointments')
    .select(APPOINTMENT_SELECT)
    .eq('id', id)
    .single();
  if (error) throw new Error(`Failed to load appointment: ${error.message}`);
  return toDetail(data as unknown as AppointmentQueryRow);
}

async function replaceParticipants(appointmentId: string, participantIds: string[]): Promise<void> {
  const removed = await supabase
    .from('appointment_participants')
    .delete()
    .eq('appointment_id', appointmentId);
  if (removed.error) throw new Error(`Failed to update appointment: ${removed.error.message}`);
  const inserted = await supabase.from('appointment_participants').insert(
    participantIds.map((collaborator_id) => ({ appointment_id: appointmentId, collaborator_id })),
  );
  if (inserted.error) throw new Error(`Failed to update appointment: ${inserted.error.message}`);
}

export async function createAppointment(input: {
  title: string;
  description?: string | null;
  starts_at: string;
  ends_at: string;
  dossier_id: string | null;
  event_type_id?: string | null;
  created_by: string | null;
  participantIds: string[];
}): Promise<AppointmentDetail> {
  const { data, error } = await supabase
    .from('appointments')
    .insert({
      title: input.title.trim(),
      description: input.description?.trim() || null,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      dossier_id: input.dossier_id,
      event_type_id: input.event_type_id ?? null,
      created_by: input.created_by,
    })
    .select('*')
    .single();
  if (error || !data) {
    throw new Error(`Failed to create appointment: ${error?.message ?? 'empty'}`);
  }
  const participants = await supabase.from('appointment_participants').insert(
    input.participantIds.map((collaborator_id) => ({
      appointment_id: data.id,
      collaborator_id,
    })),
  );
  if (participants.error) {
    await supabase.from('appointments').delete().eq('id', data.id);
    throw new Error(`Failed to create appointment: ${participants.error.message}`);
  }
  return { ...data, participantIds: input.participantIds };
}

export async function updateAppointment(
  id: string,
  input: {
    title: string;
    description?: string | null;
    starts_at: string;
    ends_at: string;
    dossier_id: string | null;
    event_type_id?: string | null;
    participantIds: string[];
  },
): Promise<AppointmentDetail> {
  const { data, error } = await supabase
    .from('appointments')
    .update({
      title: input.title.trim(),
      description: input.description?.trim() || null,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
      dossier_id: input.dossier_id,
      ...(input.event_type_id !== undefined ? { event_type_id: input.event_type_id } : {}),
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error || !data) {
    throw new Error(`Failed to update appointment: ${error?.message ?? 'empty'}`);
  }
  await replaceParticipants(id, input.participantIds);
  return { ...data, participantIds: input.participantIds };
}

export async function deleteAppointment(id: string): Promise<void> {
  const { error } = await supabase.from('appointments').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete appointment: ${error.message}`);
}
